'use client'

import { Toggle } from '@base-ui/react/toggle'
import * as stylex from '@stylexjs/stylex'
import type { ComponentPropsWithRef, ReactNode } from 'react'

import { actionStyles } from '../action'
import { colors } from '../tokens/tokens.stylex'

const styles = stylex.create({
  control: {
    backgroundColor: {
      default: colors.background,
      ':hover': colors.surfaceAlt,
      ':is([data-pressed])': colors.selected,
      ':is([data-pressed]):hover': colors.selected,
    },
    color: { default: colors.foreground, ':is([data-pressed])': colors.inverse },
    borderColor: { default: colors.borderStrong, ':is([data-pressed])': colors.selected },
  },
})

type ControlledToggleState = { pressed: boolean; defaultPressed?: never; onPressedChange(pressed: boolean): void }

type UncontrolledToggleState = { pressed?: never; defaultPressed?: boolean; onPressedChange?(pressed: boolean): void }

type ToggleState = ControlledToggleState | UncontrolledToggleState

type TextToggleContent = { children: string; 'aria-label'?: string }

type AccessibleToggleContent = { children: ReactNode; 'aria-label': string }

type ToggleContent = TextToggleContent | AccessibleToggleContent

type ToggleButtonOptions = { size?: 'sm' | 'md'; value?: string }

export type ToggleButtonProps = Omit<
  ComponentPropsWithRef<'button'>,
  'className' | 'style' | 'color' | 'children' | 'aria-label' | 'aria-pressed' | 'value'
> &
  ToggleState &
  ToggleContent &
  ToggleButtonOptions

/**
 * Apply an independent pressed action immediately; use Checkbox for submitted choices or SegmentedControl
 * for exclusive choices. Applications own any acted-on pressed state and provide an accessible label for
 * icon-only content.
 *
 * Status: supported.
 */
export function ToggleButton({ size = 'md', type = 'button', ...props }: ToggleButtonProps) {
  return <Toggle {...props} type={type} {...stylex.props(actionStyles.control, actionStyles[size], styles.control)} />
}
